import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Share Management Page — lists all active/expired share links.
 * Accessed via hamburger menu → "Geteilte Links".
 */
export default function ShareManagement({ token, onClose }) {
  const { t } = useTranslation();
  const [shares, setShares] = useState([]);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ active: 0, expired: 0, total: 0 });

  const fetchShares = async (statusFilter) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/v1/aif/share/my-shares?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setShares(data.shares || []);
        setCounts({ active: data.active_count, expired: data.expired_count, total: data.total });
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchShares(filter); }, [filter]);

  const revoke = async (id) => {
    try {
      await apiClient.delete(`/api/v1/aif/share/${id}`, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      fetchShares(filter);
    } catch { /* ApiError */ }
  };

  const STATUS_LABELS = {
    pending: t('share.statusPending'),
    accepted: t('share.statusAccepted'),
    declined: t('share.statusDeclined'),
    completed: t('share.statusCompleted'),
  };

  const TYPE_ICONS = {
    task: '\uD83D\uDCCB',
    field: '\uD83C\uDF3E',
    delivery: '\uD83D\uDE9A',
    delivery_point: '\uD83D\uDCCD',
    procurement: '\uD83D\uDCE6',
    report: '\uD83D\uDCCA',
    inspection: '\uD83D\uDD0D',
  };

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('share.management')}</span>
      </header>

      {/* Summary + Filter */}
      <div className="px-4 py-3 space-y-3">
        <p className="text-xs text-[var(--aif-text-muted)]">
          {counts.active} {t('share.active')} &middot; {counts.expired} {t('share.expired')}
        </p>
        <div className="flex gap-2">
          {['active', 'expired', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filter === f
                  ? 'bg-[var(--aif-color-primary)] text-white'
                  : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)]'
              }`}
            >
              {f === 'active' ? t('share.active') : f === 'expired' ? t('share.expired') : t('share.all')}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
          </div>
        ) : shares.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">{'\uD83D\uDD17'}</div>
            <p className="text-sm text-[var(--aif-text-muted)]">{t('share.noShares')}</p>
          </div>
        ) : (
          shares.map(s => (
            <div key={s.id} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg flex-shrink-0">{TYPE_ICONS[s.share_type] || '\uD83D\uDD17'}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{s.object_label}</div>
                    {s.recipient_name && (
                      <div className="text-xs text-[var(--aif-text-muted)]">{t('share.to')}: {s.recipient_name}</div>
                    )}
                  </div>
                </div>
                {s.is_active && (
                  <button
                    onClick={() => revoke(s.id)}
                    className="text-xs text-[var(--aif-color-danger)] hover:opacity-75 flex-shrink-0"
                  >
                    {t('share.revoke')}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 text-xs text-[var(--aif-text-muted)]">
                {s.recipient_phone && <span>{s.recipient_phone}</span>}
                <span>{t('share.views')}: {s.access_count}</span>
                <span>{STATUS_LABELS[s.response_status] || s.response_status}</span>
              </div>

              <div className="text-[10px] text-[var(--aif-text-muted)]">
                {s.is_active
                  ? `${t('share.validUntil')}: ${new Date(s.expires_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                  : s.revoked_at ? t('share.revoked') : t('share.expired')
                }
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
